import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  apiErrorText,
  apiLoadErrorText,
  useListFleetGroupsQuery,
  useListFleetModelsQuery,
  useSeedFleetGroupsMutation,
  type FleetGroupSeedResult,
} from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { useFocusBackOnClose } from '@shared/lib/useFocusBackOnClose';
import { Button } from '@shared/ui/Button';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconArrowRight, IconPlus, IconSparkles } from '@shared/ui/icons';
import { riseIn } from '@shared/theme/animations';
import {
  ButtonLink,
  CheckLabel,
  FleetChip,
  Notice,
  TextButton,
  fleetLeading,
  fleetType,
  formatCount,
  isRecord,
  stretchButtons,
  typo,
} from '@entities/fleet';
import { GroupDrawer, NEW_GROUP } from './GroupDrawer';
import { GroupTable } from './GroupTable';

/** Параметр адреса с открытой карточкой группы. */
const GROUP_PARAM = 'group';
const NARROW = '640px';

/** Метка в истории: карточку открыли из списка — закрытие возвращает «Назад». */
function isCardState(state: unknown): boolean {
  return isRecord(state) && state.fleetGroupCard === true;
}

/** Кнопка группы в списке — куда вернуть фокус, если строку перерисовали. */
const groupButton = (id: string) => `[data-group-open="${CSS.escape(id)}"]`;

const Root = styled.div`
  container: groups / inline-size;
  animation: ${riseIn} 320ms ${({ theme }) => theme.motion.easeOut} both;
`;

const Intro = styled.p`
  max-width: 760px;
  margin: 0;
  font-size: ${fleetType.base};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

const ResultBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const Count = styled.p`
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.tight};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const ActionsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-left: auto;

  @container groups (max-width: ${NARROW}) {
    flex: 1 1 100%;
    margin-left: 0;
    ${stretchButtons}
  }
`;

const Box = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Empty = styled.div`
  max-width: 680px;
  margin-top: ${({ theme }) => theme.spacing(6)};
  padding: ${({ theme }) => theme.spacing(6)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};

  & > h2 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.lead};
    font-weight: 600;
  }

  & > p {
    margin: 0 0 ${({ theme }) => theme.spacing(4)};
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > div {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};
  }

  @container groups (max-width: ${NARROW}) {
    padding: ${({ theme }) => theme.spacing(4)};

    & > div {
      ${stretchButtons}
    }
  }
`;

function ListSkeleton() {
  return (
    <Box aria-busy="true" aria-label="Загрузка групп">
      {Array.from({ length: 6 }, (_, i) => (
        <SkeletonLine key={i} $height={20} $width={i % 3 === 0 ? '45%' : '100%'} />
      ))}
    </Box>
  );
}

function seedText(r: FleetGroupSeedResult): string {
  const total = `Всего групп — ${formatCount(r.groups_total)}.`;
  if (r.groups_created === 0 && r.models_attached === 0) {
    return typo(
      `Новых должностей и свободных моделей нет — всё уже разложено по группам. ${total}`,
    );
  }
  return typo(
    `Подобрано по должностям: новых групп — ${formatCount(r.groups_created)}, моделей разложено по группам — ${formatCount(r.models_attached)}. Новым группам должность Хронума задана по профессии механизатора из паспорта. ${total} Модели, которые уже стояли в группах, не тронуты.`,
  );
}

interface FleetGroupsProps {
  canWrite: boolean;
  /** Видит ли человек ставки Экономиста — тогда в карточке группы есть ссылка на них. */
  canReadRates: boolean;
}

/**
 * Группы механизаторов (ТЗ п. 6.3.1): профессия механизатора из Хронума →
 * модели справочника. Список, подбор по должностям паспорта, карточка группы
 * в боковой панели с правкой состава, архивом и возвратом.
 */
export function FleetGroups({ canWrite, canReadRates }: FleetGroupsProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const openId = new URLSearchParams(location.search).get(GROUP_PARAM);
  useFocusBackOnClose(openId, groupButton);

  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading, isError, error, isFetching, refetch } = useListFleetGroupsQuery(
    showArchived ? { include_archived: true } : {},
  );
  const { data: fleet } = useListFleetModelsQuery({});
  const [seed, { isLoading: seeding }] = useSeedFleetGroupsMutation();
  const [seedResult, setSeedResult] = useState<FleetGroupSeedResult | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);

  const groups = data?.items ?? [];
  const active = groups.filter((g) => !g.archived);
  const modelsInGroups = active.reduce((sum, g) => sum + g.models_count, 0);
  const unitsInGroups = active.reduce((sum, g) => sum + g.units_count, 0);
  // У модели в списке справочника — её группа; без неё модель вне групп.
  const outside = fleet ? fleet.items.filter((m) => !m.group).length : null;
  const fleetEmpty = fleet !== undefined && fleet.items.length === 0;

  const withGroup = useCallback(
    (id: string | null) => {
      const p = new URLSearchParams(location.search);
      if (id) p.set(GROUP_PARAM, id);
      else p.delete(GROUP_PARAM);
      const s = p.toString();
      return s ? `?${s}` : '';
    },
    [location.search],
  );

  const openGroup = useCallback(
    (id: string) => navigate({ search: withGroup(id) }, { state: { fleetGroupCard: true } }),
    [navigate, withGroup],
  );

  const switchGroup = useCallback(
    (id: string) => navigate({ search: withGroup(id) }, { replace: true, state: location.state }),
    [navigate, withGroup, location.state],
  );

  const closeGroup = useCallback(() => {
    if (isCardState(location.state)) navigate(-1);
    else navigate({ search: withGroup(null) }, { replace: true });
  }, [navigate, withGroup, location.state]);

  const ratesHref = useCallback(
    (id: string) =>
      canReadRates
        ? `/agents/econ/group-rates?object=default&group=${encodeURIComponent(id)}`
        : null,
    [canReadRates],
  );

  async function onSeed() {
    setSeedError(null);
    setSeedResult(null);
    try {
      const res = await seed().unwrap();
      setSeedResult(res.result);
    } catch (err) {
      setSeedError(apiErrorText(err, 'Не удалось подобрать группы по должностям механизаторов.'));
    }
  }

  const seedButton = (variant: 'primary' | 'secondary') => (
    <Button
      type="button"
      $variant={variant}
      disabled={seeding || fleetEmpty}
      title={
        fleetEmpty
          ? 'Сначала заполните справочник моделей'
          : 'Новые группы — по должности механизатора в паспорте, с такой же должностью Хронума; модели, которые уже стоят в группах, не трогаются'
      }
      onClick={() => void onSeed()}
    >
      <IconSparkles size={16} />
      {seeding ? 'Подбираю…' : 'Подобрать по должностям механизаторов'}
    </Button>
  );

  const empty = !isLoading && !isError && groups.length === 0 && !showArchived;

  return (
    <Root>
      <Intro>
        {typo(
          'Группа — профессия механизатора, которую знает Хронум: «машинисты бульдозеров», «крановщики». Часы её должностей в Хронуме — маш.-часы группы, а средняя ставка группы на объекте превращает их в стоимость техники в БДР.',
        )}
      </Intro>

      {(seedResult || seedError) && (
        <Box>
          <Notice $tone={seedError ? 'danger' : 'success'} role={seedError ? 'alert' : 'status'}>
            <span className="text">{seedError ?? (seedResult ? seedText(seedResult) : '')}</span>
            <TextButton
              type="button"
              onClick={() => {
                setSeedResult(null);
                setSeedError(null);
              }}
            >
              Скрыть
            </TextButton>
          </Notice>
        </Box>
      )}

      {isError ? (
        <Box>
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(
                error,
                'Не удалось загрузить группы механизаторов.',
                'Нет доступа к справочнику техники.',
              )}
            </span>
            <TextButton type="button" onClick={() => void refetch()}>
              Повторить
            </TextButton>
          </Notice>
        </Box>
      ) : isLoading ? (
        <ListSkeleton />
      ) : empty ? (
        <Empty>
          <h2>Групп механизаторов пока нет</h2>
          <p>
            {typo(
              fleetEmpty
                ? 'Группы подбираются по должности механизатора в паспортах моделей, а справочник моделей пока пуст. Сначала загрузите книгу ЭММ на вкладке «Модели техники».'
                : canWrite
                  ? 'Подбор разложит модели справочника по группам: на каждую должность механизатора из паспортов — своя группа с такой же должностью Хронума, техника без механизатора — в группу «Без механизатора» с учётом в сутках. Потом группы можно поправить: переименовать, перенести модели, добавить должности.'
                  : 'Группы ведут экономисты: как только они подберут группы по должностям механизаторов, они появятся здесь.',
            )}
          </p>
          {canWrite && !fleetEmpty && (
            <div>
              {seedButton('primary')}
              <Button type="button" $variant="secondary" onClick={() => openGroup(NEW_GROUP)}>
                <IconPlus size={16} />
                Добавить группу вручную
              </Button>
            </div>
          )}
        </Empty>
      ) : (
        <>
          <ResultBar>
            <Count aria-live="polite">
              <strong>
                {formatCount(active.length)}{' '}
                {pluralizeRu(active.length, ['группа', 'группы', 'групп'])}
              </strong>
              {' · '}
              {formatCount(modelsInGroups)}{' '}
              {pluralizeRu(modelsInGroups, ['модель', 'модели', 'моделей'])} ·{' '}
              {formatCount(unitsInGroups)} ед.
            </Count>
            {outside !== null && outside > 0 && (
              <FleetChip
                $tone="warning"
                title="Модели вне групп не попадают в подсказку средней ставки"
              >
                вне групп: {formatCount(outside)}{' '}
                {pluralizeRu(outside, ['модель', 'модели', 'моделей'])}
              </FleetChip>
            )}
            <CheckLabel>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Показать архив
            </CheckLabel>
            <ActionsGroup>
              {canReadRates && (
                <ButtonLink to="/agents/econ/group-rates" $variant="ghost">
                  Ставки групп
                  <IconArrowRight size={16} />
                </ButtonLink>
              )}
              {canWrite && (
                <>
                  {seedButton('secondary')}
                  <Button type="button" $variant="primary" onClick={() => openGroup(NEW_GROUP)}>
                    <IconPlus size={16} />
                    Добавить группу
                  </Button>
                </>
              )}
            </ActionsGroup>
          </ResultBar>
          <Box aria-busy={isFetching}>
            {groups.length === 0 ? (
              <Intro>В архиве групп нет.</Intro>
            ) : (
              <GroupTable groups={groups} onOpen={openGroup} />
            )}
          </Box>
        </>
      )}

      {openId !== null && (openId !== NEW_GROUP || canWrite) && (
        <GroupDrawer
          groupId={openId}
          preview={groups.find((g) => g.id === openId)}
          canWrite={canWrite}
          ratesHref={ratesHref}
          onClose={closeGroup}
          onCreated={switchGroup}
        />
      )}
    </Root>
  );
}
